
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Calculator, TrendingDown, Search } from 'lucide-react';
import { fadeIn, slideIn, staggerContainer, getReducedMotionVariants } from '../utils/motion';
import { EnhancedCard } from '@/components/ui/enhanced-card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { images } from '@/assets/images';

interface ProblemSolutionPageProps {
  reducedMotion: boolean;
}

const ProblemSolutionPage: React.FC<ProblemSolutionPageProps> = ({ reducedMotion }) => {
  const painPoints = [
    {
      icon: Brain,
      title: "Qualification Uncertainty",
      description: "Homebuyers face anxiety over whether they'll be approved for a mortgage, with many discovering issues too late in the process.",
      tooltip: "93% of homebuyers report stress about mortgage qualification"
    },
    {
      icon: Calculator,
      title: "Hidden Cost Confusion",
      description: "Most buyers are caught off-guard by closing costs and fees that weren't clearly explained upfront.",
      tooltip: "First-time buyers underestimate closing costs by $6,000 on average"
    },
    {
      icon: Search,
      title: "Property Comparison Paralysis",
      description: "Comparing multiple properties across different metrics becomes overwhelming without proper tools.",
      tooltip: "Average buyer views 10+ homes before deciding"
    },
    {
      icon: TrendingDown,
      title: "Investment Risk Blindness",
      description: "Many buyers don't evaluate long-term ROI and investment potential before purchasing.",
      tooltip: "70% of buyers don't calculate investment returns"
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
      {/* Left Column - Pain Points */}
      <motion.div
        variants={getReducedMotionVariants(slideIn('left', 0, 0.6), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="flex flex-col space-y-8"
      >
        <h2 className="text-3xl font-bold">The Problem</h2>
        
        <div className="space-y-6">
          {painPoints.map((point, index) => (
            <TooltipProvider key={point.title} delayDuration={300}>
              <Tooltip>
                <motion.div
                  variants={getReducedMotionVariants(fadeIn('up', 0.2 + index * 0.1), reducedMotion)}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex items-start space-x-4"
                >
                  <div className="mt-1 bg-red-100 dark:bg-red-900/30 p-3 rounded-full">
                    <point.icon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <TooltipTrigger asChild>
                    <div className="space-y-2 cursor-help">
                      <h3 className="text-xl font-semibold">{point.title}</h3>
                      <p className="text-muted-foreground">{point.description}</p>
                    </div>
                  </TooltipTrigger>
                </motion.div>
                <TooltipContent side="top" className="max-w-xs">
                  <p>{point.tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        <div className="border-l-4 border-primary pl-4 mt-4">
          <p className="text-lg">
            "Most homebuyers enter the process blind to the financial realities, leading to frustration and expensive mistakes."
          </p>
        </div>
      </motion.div>

      {/* Right Column - Solution Image */}
      <motion.div
        variants={getReducedMotionVariants(slideIn('right', 0.2, 0.6), reducedMotion)}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        className="flex flex-col space-y-6"
      >
        <h2 className="text-3xl font-bold">The Complete Solution</h2>

        <motion.div
          variants={getReducedMotionVariants(staggerContainer(0.1, 0.2), reducedMotion)}
          className="space-y-4"
        >
          <EnhancedCard className="p-6" gradient>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={images.equiforgeFlameLogo} 
                alt="EquiForge Logo"
                className="h-12 w-auto object-contain"
                loading="lazy"
              />
              <h3 className="text-2xl font-semibold">Integrated Tool Ecosystem</h3>
            </div>
            <p className="text-muted-foreground mb-4">
              EquiForge provides a complete suite of tools working together to solve every challenge 
              in the homebuying process:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 bg-primary/5 rounded-lg">
                <h4 className="font-semibold text-sm">SmartBuy Analyzer</h4>
                <p className="text-xs text-muted-foreground">Scenario comparison & qualification</p>
              </div>
              <div className="p-3 bg-primary/5 rounded-lg">
                <h4 className="font-semibold text-sm">Investor Analytics</h4>
                <p className="text-xs text-muted-foreground">ROI projections & investment analysis</p>
              </div>
              <div className="p-3 bg-primary/5 rounded-lg">
                <h4 className="font-semibold text-sm">Property Comparison</h4>
                <p className="text-xs text-muted-foreground">Side-by-side property analysis</p>
              </div>
              <div className="p-3 bg-primary/5 rounded-lg">
                <h4 className="font-semibold text-sm">C.L.A.R.A. AI Assistant</h4>
                <p className="text-xs text-muted-foreground">24/7 AI-powered guidance</p>
              </div>
            </div>
          </EnhancedCard>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ProblemSolutionPage;
